import * as React from 'react';
import { loadCSS } from 'fg-loadcss';

/**
 * Enhanced lazy CSS loader that wraps CSS in a layer using fetch to avoid CORS issues
 * @param {string} href - URL of the CSS file to load
 * @param {string} before - CSS selector to insert before
 * @param {object} options - Additional options
 * @param {string} options.layer - Optional CSS layer name to wrap the CSS in
 * @returns {() => void} cleanup function
 */
export default function useLazyCSS(href, before) {
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  React.useEffect(function () {
    // If no layer is specified, add style and clean it on unmount
    if (!options.layer) {
      var link = loadCSS(href, document.querySelector(before));
      return function () {
        var _link$parentElement;
        (_link$parentElement = link.parentElement) == null || _link$parentElement.removeChild(link);
      };
    }

    // With layer option, we need to fetch the CSS content and wrap it
    var styleElement = null;
    var abortController = new AbortController();

    // Fetch the CSS content directly to avoid CORS issues with cssRules
    fetch(href, {
      signal: abortController.signal
    }).then(function (response) {
      if (!response.ok) {
        throw new Error("Failed to fetch CSS: ".concat(response.statusText));
      }
      return response.text();
    }).then(function (cssText) {
      // Create a style element with the CSS wrapped in the specified layer
      styleElement = document.createElement('style');
      styleElement.setAttribute('data-href', href);
      styleElement.textContent = "@layer ".concat(options.layer, " {\n").concat(cssText, "\n}");

      // Insert at the specified position
      var beforeElement = document.querySelector(before);
      if (beforeElement != null && beforeElement.parentNode) {
        beforeElement.parentNode.insertBefore(styleElement, beforeElement);
      } else {
        document.head.appendChild(styleElement);
      }
    })["catch"](function (error) {
      // Ignore abort errors, log others
      if (error !== 'useEffect' && error.name !== 'AbortError') {
        if (false) {
          console.error('Error loading CSS with layer:', error);
        }

        // Fall back to regular link element if fetch fails
        styleElement = loadCSS(href, document.querySelector(before));
      }
    });

    // Cleanup function
    return function () {
      // Cancel any pending fetch
      abortController.abort('useEffect');

      // Remove the style element if it was created
      if (styleElement && styleElement.parentElement) {
        styleElement.parentElement.removeChild(styleElement);
      }
    };
  }, [href, before, options.layer]);
}